import { RecordScreen } from "@/lib/Components/Screens/RecordScreen/RecordScreen";
import { DocumentTextIcon } from "@heroicons/react/24/outline";
import { WarningBanner } from "@/lib/Components/Banners/WarningBanner";
import { Link } from "@/components/catalyst/link";
import { ActivityIcon } from "lucide-react";
import { useInvoiceRecord } from "@/app/Invoices/Hooks/useInvoiceRecord";
import { useInvoiceActions } from "@/app/Invoices/Hooks/useInvoiceActions";
import { Badge } from "@/components/catalyst/badge";
import { useTranslations } from "use-intl";
import { invoiceStatusIntentMap } from "@/app/Invoices/Utils/invoiceStatusIntentMap";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { ArrowLeftStartOnRectangleIcon } from "@heroicons/react/20/solid";
import { Button } from "@/components/catalyst/button";
import { useNavigate } from "@tanstack/react-router";

export function InvoiceRecordScreen() {
  const invoice = useInvoiceRecord();
  const getActions = useInvoiceActions();
  const t = useTranslations("invoice");

  const workOrder = invoice.workOrders?.[0];

  return (
    <RecordScreen
      tabParams={{ id: invoice.id }}
      title={invoice.reference}
      actionsGroups={getActions(invoice)}
      badges={
        <>
          {workOrder ? (
            <Badge color={"zinc"}>
              <Link
                className="flex gap-2"
                from="/$tenant/$orgId/supplier/{-$supplierId}"
                to={
                  "/$tenant/$orgId/supplier/{-$supplierId}/pro-drivers/work-orders/$id"
                }
                params={(prev) => ({ ...prev, id: workOrder.id })}
              >
                <span className="flex items-center justify-center text-blue-400">
                  {workOrder.reference}
                </span>
              </Link>
            </Badge>
          ) : null}
          <Badge color={invoiceStatusIntentMap[invoice.status]}>
            {t(`status.${invoice.status}`)}
          </Badge>
        </>
      }
      breadcrumbs={
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink
                to="/$tenant/$orgId/supplier/{-$supplierId}/invoices"
                params={(prev) => prev}
              >
                Invoices
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>{invoice.reference}</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
      }
      banner={
        <>
          <BillingEmailMissingBanner />
        </>
      }
      tabs={[
        {
          name: "Details",
          to: "/$tenant/$orgId/supplier/{-$supplierId}/invoices/$id",
          Icon: DocumentTextIcon,
        },
        {
          name: "Activity",
          to: "/$tenant/$orgId/supplier/{-$supplierId}/invoices/$id/activity",
          Icon: ActivityIcon,
        },
      ]}
    />
  );
}

function BillingEmailMissingBanner() {
  const record = useInvoiceRecord();
  const navigate = useNavigate();

  if (record.payee.billing_email) {
    return null;
  }

  return (
    <WarningBanner
      className="mt-6"
      containerClassName="flex items-center w-full justify-between"
    >
      <span>A billing email is missing for {record.payee.name}.</span>
      <Button
        plain
        onClick={() => {
          navigate({
            to: ".",
            search: {
              contactId: record.payee.id,
            },
          });
        }}
      >
        Add email <ArrowLeftStartOnRectangleIcon />
      </Button>
    </WarningBanner>
  );
}
